import {
  DealDetail,
  HaircutType,
  MidTradeAction,
  MidTradeResponse,
  RepoType,
  ServicingType,
  SpreadsheetMapping,
  UserPreference,
  ValidationIssue
} from 'api'
import { gatherIssues, parseDate, parseEnum, parseNumber, parseString, SpreadsheetParseResult, parseDecimal } from '../upload/model'
import { Locale, Translation } from 'locale'
import { Decimal, Result } from 'utils'

export interface MidTradeEventAction {
  actionType: MidTradeAction | null
  responseType: MidTradeResponse | null
  tradeId: string | null
  effectiveDate: Date | null
  notional: number | null
  rate: number | null
  fee: number | null
  haircut: number | null
  haircutType: HaircutType | null
  dataIssues: ValidationIssue[]
}

export interface ValidMidTradeEventAction extends MidTradeEventAction {
  tradeId: string
}

interface MidTradeEventSpreadsheetParseRowResult {
  midTradeEventAction?: MidTradeEventAction
  parseIssues: ValidationIssue[]
}

export interface MidTradeEventSpreadsheetParseResult extends SpreadsheetParseResult {
  midTradeEventActions: MidTradeEventAction[]
}

export function parseMidTradeEventSpreadsheet(
  filename: string,
  rows: any[],
  spreadsheetMapping: SpreadsheetMapping,
  userPreference: UserPreference,
  incoming: boolean,
  getDealDetail: (dealId: string) => DealDetail | null
): MidTradeEventSpreadsheetParseResult {
  return rows.reduce<MidTradeEventSpreadsheetParseResult>(
    (spreadsheetResult, row, rowIdx) => {
      const rowResult = incoming
        ? parseIncomingRow(row, rowIdx + 1, spreadsheetMapping, userPreference, getDealDetail)
        : parseRow(row, rowIdx + 1, spreadsheetMapping, userPreference, getDealDetail)

      spreadsheetResult.issues.push(...rowResult.parseIssues)

      if (rowResult.midTradeEventAction && rowResult.parseIssues.length === 0) {
        spreadsheetResult.midTradeEventActions.push(rowResult.midTradeEventAction)
      }

      return spreadsheetResult
    },
    { filename, rows, issues: [], midTradeEventActions: [] }
  )
}

interface MidTradeSpreadsheetParams {
  action: Result<MidTradeAction>
  tradeId: Result<string>
  effectiveDate: Result<Date>
  notional: Result<number>
  rate: Result<number>
  haircut: Result<number>
  fee: Result<number>
  haircutType: Result<HaircutType>
  userPreference: UserPreference
}

const makeMidTradeEventAction = (override: Partial<MidTradeEventAction>): MidTradeEventAction => ({
  actionType: null,
  responseType: null,
  tradeId: null,
  effectiveDate: null,
  notional: null,
  rate: null,
  fee: null,
  haircut: null,
  haircutType: null,
  dataIssues: [],
  ...override
})

const switchMidTradeSpreadsheet = ({
  action,
  tradeId,
  effectiveDate,
  notional,
  rate,
  haircut,
  fee,
  haircutType,
  userPreference
}: MidTradeSpreadsheetParams): MidTradeEventSpreadsheetParseRowResult => {
  switch (action.value) {
    case MidTradeAction.CLOSE:
      return {
        parseIssues: gatherIssues(tradeId, action, effectiveDate, notional, rate, haircut),
        midTradeEventAction: makeMidTradeEventAction({
          tradeId: Result.getValue(tradeId),
          actionType: MidTradeAction.CLOSE,
          effectiveDate: Result.getValue(effectiveDate)
        })
      }
    case MidTradeAction.REPRICE:
      return {
        parseIssues: gatherIssues(tradeId, action, effectiveDate, notional, rate, haircut, fee),
        midTradeEventAction: makeMidTradeEventAction({
          tradeId: Result.getValue(tradeId),
          actionType: MidTradeAction.REPRICE,
          effectiveDate: Result.getValue(effectiveDate),
          rate: Result.getValue(rate),
          fee: Result.getValue(fee)
        })
      }
    case MidTradeAction.RESIZE:
    case MidTradeAction.RESIZE_WITH_RECALCULATED_COLLATERAL_PRICE:
      const haircutTypeValue = Result.getValue(haircut) ? Result.getValue(haircutType) || userPreference.defaultHaircutType : null
      return {
        parseIssues: gatherIssues(tradeId, action, effectiveDate, notional, rate, haircut),
        midTradeEventAction: makeMidTradeEventAction({
          tradeId: Result.getValue(tradeId),
          actionType: MidTradeAction.RESIZE,
          effectiveDate: Result.getValue(effectiveDate),
          rate: Result.getValue(rate),
          fee: Result.getValue(fee),
          notional: Result.getValue(notional),
          haircut: Result.getValue(haircut),
          haircutType: haircutTypeValue
        })
      }
    case MidTradeAction.ROLL:
      return {
        parseIssues: gatherIssues(tradeId, action),
        midTradeEventAction: makeMidTradeEventAction({ tradeId: Result.getValue(tradeId), actionType: MidTradeAction.ROLL })
      }
    default:
      throw new Error(`Parsing MTE spreadsheet - unsupported mid trade event action [${action.value}]`)
  }
}

const parseRow = (
  row: any,
  rowNum: number,
  spreadsheetMapping: SpreadsheetMapping,
  userPreference: UserPreference,
  getDealDetail: (dealId: string) => DealDetail | null
): MidTradeEventSpreadsheetParseRowResult => {
  const action = parseEnum<MidTradeAction>(
    row,
    rowNum,
    spreadsheetMapping.action,
    spreadsheetMapping.action.values,
    Locale.translate(Translation.TC_ACTION),
    false,
    true
  )
  const tradeId = parseString(row, rowNum, spreadsheetMapping.tradeId, Locale.translate(Translation.TC_DEAL_ID), true)
  const effectiveDate = parseDate(row, rowNum, spreadsheetMapping.effectiveDate, Locale.translate(Translation.TC_EFFECTIVE_DATE))
  const notional = parseDecimal(row, rowNum, spreadsheetMapping.notional, Locale.translate(Translation.TC_NOTIONAL, { amountFormat: null }))
  const rate = parseNumber(row, rowNum, spreadsheetMapping.rate, Locale.translate(Translation.TC_RATE))
  const fee = parseNumber(row, rowNum, spreadsheetMapping.fee, Locale.translate(Translation.TC_FEE))
  const haircut = parseNumber(row, rowNum, spreadsheetMapping.haircut, Locale.translate(Translation.TC_HAIRCUT))
  const haircutType = parseEnum<HaircutType>(
    row,
    rowNum,
    spreadsheetMapping.haircutType,
    spreadsheetMapping.haircutType.values,
    Locale.translate(Translation.TC_HAIRCUT_TYPE)
  )

  const repoType = tradeId.isValue ? getDealDetail(tradeId.value)?.deal.repoType ?? null : null

  if (!action.isValue) {
    let result = {
      parseIssues: gatherIssues(tradeId, action, effectiveDate, notional, rate, haircut),
      midTradeEventAction: {
        actionType: null,
        responseType: null,
        tradeId: tradeId.isValue ? tradeId.value : null,
        effectiveDate: effectiveDate.isValue ? effectiveDate.value : null,
        notional: notional.isValue ? notional.value : null,
        rate: rate.isValue ? rate.value : null,
        fee: fee.isValue ? fee.value : null,
        haircut: haircut.isValue ? haircut.value : null,
        haircutType: haircutType.isValue ? haircutType.value : null,
        dataIssues: []
      }
    }

    return updateRateOrFee(result, repoType)
  }

  let parsedResult = switchMidTradeSpreadsheet({
    action,
    tradeId,
    effectiveDate,
    notional,
    rate,
    haircut,
    fee,
    haircutType,
    userPreference
  })
  return updateRateOrFee(parsedResult, repoType)
}

interface MidTradeSpreadsheetRowParams {
  response: Result<MidTradeResponse>
  tradeId: Result<string>
  effectiveDate: Result<Date>
  notional: Result<number>
  rate: Result<number>
  haircut: Result<number>
  fee: Result<number>
  haircutType: Result<HaircutType>
  userPreference: UserPreference
  servicingType: ServicingType | null
}

const switchMidTradeSpreadsheetRow = ({
  response,
  tradeId,
  effectiveDate,
  notional,
  rate,
  haircut,
  fee,
  haircutType,
  userPreference,
  servicingType
}: MidTradeSpreadsheetRowParams): MidTradeEventSpreadsheetParseRowResult => {
  switch (response.value) {
    case MidTradeResponse.ACCEPT:
    case MidTradeResponse.REJECT:
      return {
        parseIssues: gatherIssues(tradeId, response, effectiveDate, notional, rate, haircut),
        midTradeEventAction: makeMidTradeEventAction({ tradeId: Result.getValue(tradeId), responseType: Result.getValue(response) })
      }
    case MidTradeResponse.SEND:
      switch (servicingType) {
        case ServicingType.TERMINATION:
          return {
            parseIssues: gatherIssues(tradeId, response, effectiveDate, notional, rate, haircut, fee),
            midTradeEventAction: makeMidTradeEventAction({ tradeId: Result.getValue(tradeId), responseType: MidTradeResponse.SEND })
          }
        case ServicingType.RERATE:
          return {
            parseIssues: gatherIssues(tradeId, response, effectiveDate, notional, rate, haircut, fee),
            midTradeEventAction: makeMidTradeEventAction({
              tradeId: Result.getValue(tradeId),
              effectiveDate: Result.getValue(effectiveDate),
              rate: Result.getValue(rate),
              fee: Result.getValue(fee),
              responseType: MidTradeResponse.SEND
            })
          }
        case ServicingType.CLOSE_OPEN:
          const haircutTypeValue = Result.getValue(haircut) ? Result.getValue(haircutType) || userPreference.defaultHaircutType : null

          return {
            parseIssues: gatherIssues(tradeId, response, effectiveDate, notional, rate, haircut, fee),
            midTradeEventAction: makeMidTradeEventAction({
              tradeId: Result.getValue(tradeId),
              effectiveDate: Result.getValue(effectiveDate),
              rate: Result.getValue(rate),
              fee: Result.getValue(fee),
              notional: Result.getValue(notional),
              haircut: Result.getValue(haircut),
              haircutType: haircutTypeValue,
              responseType: MidTradeResponse.SEND
            })
          }
        default:
          throw new Error(`Parsing MTE spreadsheet - unsupported mid trade event servicing type [${servicingType}]`)
      }
    default:
      throw new Error(`Parsing MTE spreadsheet - unsupported mid trade event response [${response.value}]`)
  }
}

/*
  In spreadsheet uploads, we want to correct the user in case they uploaded the incorrect field for their repo type
*/
const updateRateOrFee = (
  rowResult: MidTradeEventSpreadsheetParseRowResult,
  repoType: RepoType | null
): MidTradeEventSpreadsheetParseRowResult => {
  if (repoType === RepoType.STOCK_BORROWING_LENDING && rowResult.midTradeEventAction) {
    rowResult.midTradeEventAction.fee = rowResult.midTradeEventAction.fee || rowResult.midTradeEventAction.rate
    rowResult.midTradeEventAction.rate = null
  } else if (rowResult.midTradeEventAction) {
    rowResult.midTradeEventAction.rate = rowResult.midTradeEventAction.rate || rowResult.midTradeEventAction.fee
    rowResult.midTradeEventAction.fee = null
  }
  if (rowResult.midTradeEventAction?.fee) {
    rowResult.midTradeEventAction.fee = Decimal.divide(rowResult.midTradeEventAction.fee, 10000)
  }
  return rowResult
}

const parseIncomingRow = (
  row: any,
  rowNum: number,
  spreadsheetMapping: SpreadsheetMapping,
  userPreference: UserPreference,
  getDealDetail: (dealId: string) => DealDetail | null
): MidTradeEventSpreadsheetParseRowResult => {
  const response = parseEnum<MidTradeResponse>(
    row,
    rowNum,
    spreadsheetMapping.response,
    spreadsheetMapping.response.values,
    Locale.translate(Translation.TC_RESPONSE),
    false,
    true
  )
  const tradeId = parseString(row, rowNum, spreadsheetMapping.tradeId, Locale.translate(Translation.TC_DEAL_ID), true)
  const effectiveDate = parseDate(row, rowNum, spreadsheetMapping.effectiveDate, Locale.translate(Translation.TC_EFFECTIVE_DATE))
  const notional = parseDecimal(row, rowNum, spreadsheetMapping.notional, Locale.translate(Translation.TC_NOTIONAL, { amountFormat: null }))
  const rate = parseNumber(row, rowNum, spreadsheetMapping.rate, Locale.translate(Translation.TC_RATE))
  const fee = parseNumber(row, rowNum, spreadsheetMapping.fee, Locale.translate(Translation.TC_RATE))
  const haircut = parseNumber(row, rowNum, spreadsheetMapping.haircut, Locale.translate(Translation.TC_HAIRCUT))
  const haircutType = parseEnum<HaircutType>(
    row,
    rowNum,
    spreadsheetMapping.haircutType,
    spreadsheetMapping.haircutType.values,
    Locale.translate(Translation.TC_HAIRCUT_TYPE)
  )

  const servicingType = tradeId.isValue ? getDealDetail(tradeId.value)?.deal.servicingDetails?.servicingType?.servicingType ?? null : null
  const repoType = tradeId.isValue ? getDealDetail(tradeId.value)?.deal.repoType ?? null : null
  if (
    !response.isValue ||
    !servicingType ||
    ![ServicingType.CLOSE_OPEN, ServicingType.RERATE, ServicingType.TERMINATION].includes(servicingType)
  ) {
    const result = {
      parseIssues: gatherIssues(tradeId, response, effectiveDate, notional, rate, haircut, fee),
      midTradeEventAction: {
        actionType: null,
        responseType: response.isValue ? response.value : null,
        tradeId: tradeId.isValue ? tradeId.value : null,
        effectiveDate: effectiveDate.isValue ? effectiveDate.value : null,
        notional: notional.isValue ? notional.value : null,
        rate: rate.isValue ? rate.value : null,
        fee: fee.isValue ? fee.value : null,
        haircut: haircut.isValue ? haircut.value : null,
        haircutType: haircutType.isValue ? haircutType.value : null,
        dataIssues: []
      }
    }
    return updateRateOrFee(result, repoType)
  }

  const parsedResult = switchMidTradeSpreadsheetRow({
    response,
    tradeId,
    effectiveDate,
    notional,
    rate,
    haircut,
    fee,
    haircutType,
    userPreference,
    servicingType
  })

  return updateRateOrFee(parsedResult, repoType)
}
